import { OptionsMenuStorybook } from "@finanedi.es/storybook"
import {
  faArrowDownToBracket,
  faCoins,
  faMemo,
  faMemoCircleCheck,
  faTrashCan,
} from "@fortawesome/pro-light-svg-icons"
import { FacturaReadingFiltersDtoProps } from "api/DTOs/FacturaReadingFiltersDto"
import { ESTADO_REVISION_DOCUMENTO_COMERCIAL } from "api/Types/EstadoRevisionDocumentoComercial"
import { ESTADOS_COBRO } from "api/Types/EstadosCobro"
import { formatCurrency } from "common/utils/currency"
import { EstadoRevisionActual } from "features/Documentos/components/EstadoRevisionActual/EstadoRevisionActual"
import { ModalTypeDownloadDocumentProps } from "features/Documentos/components/ModalTypeDownloadDocument/constant"
import { TFunction } from "i18next"
import { frontendDateFormat } from "utils/formatDate"
import { EstadoCobroPagoFactura } from "../components/EstadoCobroPago"
import { EstadoFinanciado } from "../components/EstadoFinanciado"
import { EstadoOrigenFactura } from "../components/EstadoOrgien"

interface ColumnasFacturasParams {
  t: TFunction
  setShowModalDownload: React.Dispatch<
    React.SetStateAction<ModalTypeDownloadDocumentProps["modalState"]>
  >
  setShowModalPagoCobroManual: React.Dispatch<
    React.SetStateAction<{
      show: boolean
      id: string | undefined
      serie: string | null | undefined
    }>
  >
  rectificarOeditarFactura: (
    docAsociado?: string | null,
    id?: string,
    esBorrador?: boolean
  ) => Promise<void>
  showFinanciadaColumn: boolean
  showContacto?: boolean
  toggleDeleteModal?: (id?: string) => void
}

export const COLUMNAS_FACTURAS_ENVIADAS_ERP = ({
  t,
  setShowModalDownload,
  setShowModalPagoCobroManual,
  rectificarOeditarFactura,
  showFinanciadaColumn,
  showContacto = true,
  toggleDeleteModal = () => undefined,
}: ColumnasFacturasParams) => {
  const columns = [
    {
      header: t("crear.factura.tab.headerEmisionDate"),
      field: "fechaEmision",
      key: "fechaEmision",
      body: ({ fechaEmision }: FacturaReadingFiltersDtoProps) =>
        fechaEmision ? frontendDateFormat(new Date(fechaEmision)) : "",
      style: { width: "10%" },
      sortable: true,
      className: "!tw-text-start",
      bodyClassName: "!tw-text-start",
      sortField: "fechaEmision",
    },
    {
      header: t("crear.factura.tab.headerNumOrder"),
      field: "serie",
      key: "serie",
      style: { width: "15%" },
      sortable: true,
      sortField: "serie",
    },
    showContacto
      ? {
          header: t("crear.factura.tab.headerCliente"),
          key: "cliente",
          field: "Librado.RazonSocial",
          body: (rowData: FacturaReadingFiltersDtoProps) => {
            return `${rowData.razonSocialLibrado} (${rowData.nifLibrado})`
          },
          sortable: true,
          sortField: "Librado.RazonSocial",
        }
      : {},
    {
      header: t("crear.factura.tab.headerState"),
      field: "estadoRevisionActual",
      key: "estadoRevisionActual",
      body: ({ estadoRevisionActual }: FacturaReadingFiltersDtoProps) => {
        return estadoRevisionActual
          ? EstadoRevisionActual(estadoRevisionActual)
          : ""
      },
      style: { width: "11%" },
      sortable: true,
      alignCol: "center",
      sortField: "estadoRevisionActual",
    },
    {
      header: t("crear.factura.tab.headerImport"),
      field: "importe",
      key: "importe",
      className: "!tw-text-end",
      style: { width: "11%" },
      body: ({ importe }: FacturaReadingFiltersDtoProps) =>
        formatCurrency(importe),
      sortable: true,
      alignCol: "right",
      sortField: "importe",
    },
    {
      header: t("crear.factura.tab.headerStatusPayCobro"),
      key: "estadoCobro",
      field: "EstadoCobroFactura.EstadoCobro",
      style: { width: "200px" },
      body: (rowData: FacturaReadingFiltersDtoProps) =>
        EstadoCobroPagoFactura({ rowData, isVenta: true, t }),
      sortable: true,
      alignCol: "right",
      bodyClassName: "!tw-text-end ",
      sortField: "EstadoCobroFactura.EstadoCobro",
    },
    ...(showFinanciadaColumn
      ? [
          {
            header: t("crear.factura.tab.headerFinanciar"),
            key: "estadoOperaciones",
            field: "estadoOperaciones",
            style: { width: "5%" },
            className: "!tw-text-end",
            bodyClassName: "!tw-text-end",
            headerClassName: "!tw-text-end",
            body: ({ estadoOperaciones }: FacturaReadingFiltersDtoProps) =>
              EstadoFinanciado(
                Boolean(
                  estadoOperaciones?.estadoFinanciacionEfecto ??
                    estadoOperaciones?.estadoOperacion
                )
              ),
            alignCol: "center",
            sorter: false,
          },
        ]
      : []),
    {
      header: t("crear.factura.tab.headerOrigin"),
      key: "origen",
      field: "origen",
      style: { width: "5%" },
      body: (rowData: FacturaReadingFiltersDtoProps) =>
        EstadoOrigenFactura({ origen: rowData.origen, id: rowData.id, t }),
      alignCol: "center",
      bodyClassName: "!tw-text-center",
    },
    {
      key: "opciones",
      style: { width: "4%" },
      className: "!tw-p-2",
      bodyClassName: "!tw-w-auto tw-flex tw-justify-end",
      body: (rowData: FacturaReadingFiltersDtoProps) => {
        if (!rowData.id) return null
        const isInvoiceCorrected =
          rowData?.facturaRectificada?.id || rowData?.facturaRectificativa?.id
        return (
          <OptionsMenuStorybook
            id={rowData.id}
            items={[
              {
                title: `${t("crear.factura.tab.downloadLabel")}`,
                hasRedirect: true,
                action: () => {
                  setShowModalDownload({
                    show: true,
                    id: rowData.id,
                    serie: `${rowData.serie}`,
                  })
                },
                icon: faArrowDownToBracket,
              },
              {
                title: `${t("crear.factura.tab.createAmendmentInvoice")}`,
                hasRedirect: true,
                show:
                  rowData?.estadoRevisionActual !==
                    ESTADO_REVISION_DOCUMENTO_COMERCIAL.BORRADOR &&
                  !isInvoiceCorrected,
                action: () =>
                  rectificarOeditarFactura(rowData?.serie, rowData?.id),
                icon: faMemoCircleCheck,
              },
              {
                title: `${t("crear.factura.tab.markCollected")}`,
                hasRedirect: true,
                action: () =>
                  setShowModalPagoCobroManual({
                    show: true,
                    id: rowData?.id,
                    serie: rowData?.serie,
                  }),
                show:
                  rowData.estadoCobro !== ESTADOS_COBRO.COBRADO &&
                  rowData.estadoRevisionActual !==
                    ESTADO_REVISION_DOCUMENTO_COMERCIAL.BORRADOR,
                icon: faCoins,
              },
              {
                title: `${t("crear.factura.tab.editInvoiceDraft")}`,
                hasRedirect: true,
                show:
                  rowData?.estadoRevisionActual ===
                  ESTADO_REVISION_DOCUMENTO_COMERCIAL.BORRADOR,
                action: () =>
                  rectificarOeditarFactura(rowData?.serie, rowData?.id, true),
                icon: faMemo,
              },
              ...(rowData?.puedeBorrarse
                ? [
                    {
                      title: `${t("nsAssets.detailHeader.menu.delete")}`,
                      hasRedirect: false,
                      action: () => toggleDeleteModal(rowData.id),
                      icon: faTrashCan,
                      className: "tw-text-danger",
                      iconClassName: "tw-text-danger",
                    },
                  ]
                : []),
            ]}
            position='right'
          />
        )
      },
    },
  ]

  return columns
}
